import baseUrl from "./apiBaseUrl";
import {
  decodeStoredToken, getStoredAuthIdentity, getStoredAuthSession, getStoredAuthToken,
  persistAuthSession, persistAuthToken, removeAuthToken,
} from "./authTokenStorage";

export const AUTH_RECOVERY_EVENT = "ee-auth-recovery";
export const SESSION_WARNING_EVENT = "ee-session-warning";
const LOCK_KEY = "eeSessionLock";
const ACTIVITY_INTERVAL = 30000;
let refreshPromise;
let activityPromise;
let activityTimer;
let lastActivitySent = 0;
let activitySessionId;
let recoveryIdentity;
let recoveryUntil = 0;
let warning = false;

export function authenticationRecoveryError(message = "Authentication required") {
  return Object.assign(new Error(message), { code: 401, authenticationRecovery: true });
}

export function isAuthenticationRecovery(error) {
  return Boolean(error && error.authenticationRecovery);
}

export function isAuthRecoveryActive() {
  return Boolean(recoveryIdentity && !getStoredAuthToken() && Date.now() < recoveryUntil);
}

export function getSessionWarning() { return warning; }

export function getPendingLogout() {
  return JSON.parse(localStorage.getItem("eePendingLogout") || "null");
}

function setWarning(value) {
  warning = value;
  window.dispatchEvent(new Event(SESSION_WARNING_EVENT));
}

export function getLoginNotice() {
  return sessionStorage.getItem("eeLoginReason");
}

export function takeReturnDestination(portal) {
  const destination = sessionStorage.getItem("eeLoginReturn");
  sessionStorage.removeItem("eeLoginReturn");
  sessionStorage.removeItem("eeLoginReason");
  recoveryIdentity = null;
  localStorage.removeItem("eeAuthRecoveryReason");
  return destination && (destination.startsWith(`${portal}/`) || destination === portal) ? destination : portal;
}

export function recoverAuthentication(reason, expectedIdentity = getStoredAuthIdentity()) {
  if (getStoredAuthIdentity() !== expectedIdentity) return;
  if (recoveryIdentity === expectedIdentity && !getStoredAuthToken()) return;
  const decoded = decodeStoredToken();
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  recoveryIdentity = expectedIdentity || "missing";
  recoveryUntil = Date.now() + 1500;
  if (/^\/(manage|client)(\/|$)/.test(path)) sessionStorage.setItem("eeLoginReturn", path);
  sessionStorage.setItem("eeLoginReason", reason);
  localStorage.setItem("eeAuthRecoveryReason", reason);
  removeAuthToken();
  window.dispatchEvent(new CustomEvent(AUTH_RECOVERY_EVENT, { detail: { reason, role: decoded && decoded.role } }));
}

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

// Serialize cookie mutations across tabs, including composite login/recovery operations.
/* eslint-disable no-await-in-loop -- Lease acquisition is deliberately serialized. */
export async function withSessionLock(operation) {
  if (navigator.locks && navigator.locks.request) return navigator.locks.request("ee-session", operation);
  const owner = `${Date.now()}-${Math.random()}`;
  const deadline = Date.now() + 35000;
  while (Date.now() < deadline) {
    let lease;
    try { lease = JSON.parse(localStorage.getItem(LOCK_KEY)); } catch (error) { lease = null; }
    if (!lease || lease.until <= Date.now()) {
      localStorage.setItem(LOCK_KEY, JSON.stringify({ owner, until: Date.now() + 90000 }));
      await delay(30 + Math.random() * 30);
      const acquired = JSON.parse(localStorage.getItem(LOCK_KEY) || "null");
      if (acquired && acquired.owner === owner) {
        const renew = setInterval(() => {
          const current = JSON.parse(localStorage.getItem(LOCK_KEY) || "null");
          if (current && current.owner === owner) localStorage.setItem(LOCK_KEY, JSON.stringify({ owner, until: Date.now() + 90000 }));
        }, 10000);
        try { return await operation(); } finally {
          clearInterval(renew);
          const current = JSON.parse(localStorage.getItem(LOCK_KEY) || "null");
          if (current && current.owner === owner) localStorage.removeItem(LOCK_KEY);
        }
      }
    }
    await delay(60);
  }
  throw new Error("Session request timed out");
}
/* eslint-enable no-await-in-loop */

export async function credentialRequest(path, options = {}, readResponse = response => response) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await global.fetch(`${baseUrl}${path}`, {
      ...options,
      credentials: "include",
      signal: controller.signal,
      headers: { ...options.headers, "content-type": "application/json", "X-EE-Session": "1" },
    });
    // Keep the same deadline while receiving the body, before releasing the cross-tab lock.
    return await readResponse(response);
  } finally { clearTimeout(timeout); }
}

async function sessionCommand(command, identity, body = {}) {
  const { response, payload } = await credentialRequest(`session/${command}`, { method: "POST", body: JSON.stringify(body) }, async received => {
    if (getStoredAuthIdentity() !== identity) throw authenticationRecoveryError("Session changed");
    return { response: received, payload: await received.json() };
  });
  if (getStoredAuthIdentity() !== identity) throw authenticationRecoveryError("Session changed");
  if (response.status === 401) {
    recoverAuthentication(payload.code === "SESSION_EXPIRED" ? "expired" : "required", identity);
    throw authenticationRecoveryError();
  }
  if (!response.ok) {
    setWarning(true);
    throw Object.assign(new Error(payload.message || "Session unavailable"), { code: response.status });
  }
  setWarning(false);
  return payload;
}

export function accessTokenNeedsRefresh(token = getStoredAuthToken()) {
  const decoded = decodeStoredToken(token);
  const lifetime = decoded && decoded.iat ? Math.max(0, decoded.exp - decoded.iat) * 1000 : 100000;
  return Boolean(decoded && decoded.exp && decoded.exp * 1000 <= Date.now() + Math.min(10000, lifetime / 10));
}

function getPendingRefresh() {
  try { return JSON.parse(localStorage.getItem("eeRefreshAttempt")); } catch (error) { return null; }
}

function refreshAttempt(sessionId) {
  const pending = getPendingRefresh();
  if (pending && pending.id === sessionId && /^[a-f0-9]{64}$/.test(pending.requestId)) return pending;
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  const attempt = { id: sessionId, requestId: Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("") };
  localStorage.setItem("eeRefreshAttempt", JSON.stringify(attempt));
  return attempt;
}

async function renewStoredSession(session, identity) {
  const attempt = refreshAttempt(session.id);
  const payload = await sessionCommand("refresh", identity, { sessionId: session.id, requestId: attempt.requestId });
  persistAuthSession(payload.session);
  persistAuthToken(payload.token);
  localStorage.removeItem("eeRefreshAttempt");
  return payload.token;
}

export function refreshAccessToken(expectedToken = getStoredAuthToken()) {
  const identity = getStoredAuthIdentity();
  if (expectedToken !== getStoredAuthToken()) return Promise.resolve(getStoredAuthToken());
  const session = getStoredAuthSession();
  if (!session) {
    recoverAuthentication(accessTokenNeedsRefresh(expectedToken) ? "expired" : "required", identity);
    return Promise.reject(authenticationRecoveryError());
  }
  if (refreshPromise) return refreshPromise;
  refreshPromise = withSessionLock(async () => {
    if (getStoredAuthIdentity() !== identity) throw authenticationRecoveryError("Session changed");
    if (getStoredAuthToken() !== expectedToken) return getStoredAuthToken();
    return renewStoredSession(session, identity);
  }).catch((error) => {
    if (!isAuthenticationRecovery(error)) setWarning(true);
    throw error;
  }).finally(() => { refreshPromise = null; });
  return refreshPromise;
}

// Called only by actual UI interactions, never by refreshes, polling or visibility changes.
export function reportUserActivity() {
  const session = getStoredAuthSession();
  const identity = getStoredAuthIdentity();
  if (!session || !getStoredAuthToken()) return Promise.resolve();
  if (activitySessionId !== session.id) { activitySessionId = session.id; lastActivitySent = 0; clearTimeout(activityTimer); }
  const elapsed = Date.now() - lastActivitySent;
  if (activityPromise || (elapsed < ACTIVITY_INTERVAL && session.idleExpiresAt - Date.now() > ACTIVITY_INTERVAL)) {
    if (!activityTimer) {
      activityTimer = setTimeout(() => {
        activityTimer = null;
        if (getStoredAuthIdentity() === identity) reportUserActivity().catch(() => undefined);
      }, Math.max(100, ACTIVITY_INTERVAL - elapsed));
    }
    return activityPromise || Promise.resolve();
  }
  clearTimeout(activityTimer);
  activityTimer = null;
  lastActivitySent = Date.now();
  activityPromise = withSessionLock(async () => {
    if (getStoredAuthIdentity() !== identity) return;
    const pending = getPendingRefresh();
    // Recover an undelivered rotation before using the browser's possibly older cookie.
    if (pending && pending.id === session.id) await renewStoredSession(session, identity);
    const shared = JSON.parse(localStorage.getItem("eeActivityReport") || "null");
    if (shared && shared.id === session.id && Date.now() - shared.at < ACTIVITY_INTERVAL && session.idleExpiresAt - Date.now() > ACTIVITY_INTERVAL) return;
    const payload = await sessionCommand("activity", identity, { sessionId: session.id });
    localStorage.setItem("eeActivityReport", JSON.stringify({ id: session.id, at: Date.now() }));
    persistAuthSession(payload.session);
  }).catch((error) => {
    if (!isAuthenticationRecovery(error)) { lastActivitySent = 0; setWarning(true); }
    throw error;
  }).finally(() => { activityPromise = null; });
  return activityPromise;
}

export async function flushPendingLogout() {
  const pending = getPendingLogout();
  if (!pending) return;
  const response = await credentialRequest("session/logout", { method: "POST", body: JSON.stringify({ sessionId: pending.id }) });
  if (!response.ok && response.status !== 401) throw Object.assign(new Error("Session logout unavailable"), { code: response.status });
  if (getPendingLogout() && getPendingLogout().id === pending.id) localStorage.removeItem("eePendingLogout");
  setWarning(false);
}

export function retryPendingLogout() {
  return withSessionLock(flushPendingLogout).catch((error) => { setWarning(true); throw error; });
}

export function revokeStoredSession() {
  localStorage.removeItem("eeAuthRecoveryReason");
  sessionStorage.removeItem("eeLoginReason");
  const session = getStoredAuthSession();
  if (!session) return Promise.resolve();
  clearTimeout(activityTimer);
  activityTimer = null;
  localStorage.setItem("eePendingLogout", JSON.stringify({ id: session.id }));
  // Captured id prevents a delayed logout from revoking a later login.
  return retryPendingLogout();
}
